"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { getBlogPosts } from "@/lib/blog";

export default function BlogSection() {
  const [displayPosts, setDisplayPosts] = useState([]);

  // Load posts on mount and refresh when page becomes visible
  useEffect(() => {
    const loadPosts = async () => {
      try {
        const blogPosts = await getBlogPosts();
        // Show up to 3 latest posts
        setDisplayPosts(blogPosts.slice(0, 3));
      } catch (error) {
        console.error("Error loading blog posts:", error);
      }
    };

    // Load posts immediately
    loadPosts();

    // Refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadPosts();
      }
    };

    // Listen for custom event when posts are updated in the same tab
    const handleBlogPostsUpdated = () => {
      // Small delay to ensure the array is updated
      setTimeout(loadPosts, 50);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blogPostsUpdated", handleBlogPostsUpdated);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blogPostsUpdated", handleBlogPostsUpdated);
    };
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <section className="py-20 bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-6">
            Latest from Our Blog
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Stay updated with the latest insights, trends, and tips in mobile
            app development.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayPosts.map((post, index) => (
            <article
              key={post.id}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="h-48 bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">
                      {post.title.charAt(0)}
                    </span>
                  </div>
                  <span className="text-primary-600 font-medium text-sm">
                    {post.category}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center space-x-4 text-sm text-secondary-500 mb-4">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(post.publishedAt)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                <h3 className="text-xl font-semibold text-secondary-900 mb-3 line-clamp-2">
                  {post.title}
                </h3>

                <p className="text-secondary-600 mb-6 line-clamp-3">
                  {post.excerpt}
                </p>

                <Link
                  href={`/blog/${post.slug}`}
                  className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  <span>Read More</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            href="/blog"
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <span>View All Posts</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
